import {
  AgreementsOutlet,
  EditAgreement,
  ListAgreements,
} from "@poseidon/features/feature/Agreements";
import {
  ApplicationsOutlet,
  FileManagementPage,
  ListApplications,
} from "@poseidon/features/feature/Applications";
import {
  AdminProfile,
  Layout as AuthenticationLayout,
  RequireAuth,
} from "@poseidon/features/feature/Authentication";
import { Boats, ListBoats } from "@poseidon/features/feature/Boats";
import {
  CreateDealer,
  DealerOutlet,
  EditDealer,
  ListDealer,
} from "@poseidon/features/feature/Dealers";
import Rates from "@poseidon/features/feature/Rates/Rates";
import { CaisReport } from "@poseidon/features/feature/Reports";
import {
  AddressManagement,
  FileManagementPage as SailorFileManagement,
  ListSailors,
} from "@poseidon/features/feature/Sailors";
import {
  Layout as UserLayout,
  ListUsers,
} from "@poseidon/features/feature/Users";
import OutletWithCrumb from "@poseidon/ui/app/crumbs/OutletWithCrumb";
import { DashboardLayout } from "@poseidon/ui/app/layout/DashboardLayout";
import { Navigate, Route, Routes } from "react-router-dom";
import EditBoat from "@poseidon/features/feature/Boats/pages/EditBoat";
import { getUserLabel } from "@poseidon/features/feature/Users/util";
import ApplicationForm from "@poseidon/features/feature/Applications/pages/edit/ApplicationForm";
import { CreditUnderwritingSection } from "@poseidon/features/feature/Applications/pages/creditUnderwriting/CreditUnderwritingSection";
import TransactionsPage from "@poseidon/features/feature/Agreements/pages/TransactionsPage";
import PaymentsPage from "@poseidon/features/feature/Agreements/pages/PaymentsPage";
import SettlementsPage from "@poseidon/features/feature/Agreements/pages/SettlementsPage";
import ArrearsMgmt from "@poseidon/features/feature/Agreements/pages/ArrearsMgmt";
import SailorsOutlet from "@poseidon/features/feature/Sailors/SailorsOutlet";
import { SailorRiskProfileIdentity } from "@poseidon/features/feature/Sailors/pages/SailorRiskProfileIdentity";
import { SailorForm } from "@poseidon/features/feature/Sailors/pages/SailorForm";
import SailorApplications from "@poseidon/features/feature/Sailors/pages/SailorApplications";
import SailorAgreements from "@poseidon/features/feature/Sailors/pages/SailorAgreements";
import { useSailorContext } from "@poseidon/features/feature/Sailors/SailorContext";
import { useAgreementSailorContext } from "@poseidon/features/feature/Sailors/AgreementSailorContext";
import config from "@poseidon/utils/config";
import { SailorRiskProfileCreditUnderwriting } from "@poseidon/features/feature/Sailors/pages/SailorRiskProfileCreditUnderwriting";

function getSailorRoutes(baseRoutePath, listDisabled) {
  return (
    <Route
      path="sailors"
      element={
        <SailorsOutlet
          baseRoutePath={baseRoutePath}
          listDisabled={listDisabled}
        />
      }
    >
      {listDisabled || <Route index element={<ListSailors />} />}
      <Route
        path=":sailorId"
        element={
          <OutletWithCrumb
            to="/sailors/:sailorId"
            docRef="/sailors/{sailorId}"
            crumb={({ data: sailor }) => getUserLabel(sailor)}
          />
        }
      >
        <Route index element={<SailorForm context={useSailorContext} />} />
        <Route path="identityChecks" element={<SailorRiskProfileIdentity />} />
        <Route
          path="creditUnderwriting"
          element={<SailorRiskProfileCreditUnderwriting />}
        />
        <Route path="addresses" element={<AddressManagement />} />
        <Route path="applications" element={<SailorApplications />} />
        <Route path="agreements" element={<SailorAgreements />} />
        <Route path="addresses/:addressId" element={<AddressManagement />} />
        <Route
          path=":fileGroup/files"
          element={<SailorFileManagement fileCategory="sailor" />}
        />
      </Route>
    </Route>
  );
}

function getAgreementSailorRoutes(baseRoutePath, listDisabled) {
  return (
    <Route
      path="agreement-sailors"
      element={
        <SailorsOutlet
          baseRoutePath={baseRoutePath}
          listDisabled={listDisabled}
        />
      }
    >
      <Route
        path=":sailorId"
        element={
          <OutletWithCrumb
            to="/sailors/:sailorId"
            docRef="/sailors/{sailorId}"
            crumb={({ data: sailor }) => getUserLabel(sailor)}
          />
        }
      >
        <Route
          index
          element={
            <SailorForm
              context={useAgreementSailorContext}
              isAgreementSailor={true}
            />
          }
        />
      </Route>
    </Route>
  );
}

function getBoatRoutes(baseRoutePath, listDisabled) {
  return (
    <Route
      path="boats"
      element={
        <Boats baseRoutePath={baseRoutePath} listDisabled={listDisabled} />
      }
    >
      {listDisabled || <Route index element={<ListBoats />} />}
      <Route path=":boatId" element={<EditBoat />} />
      <Route
        path=":boatId/:fileGroup/files"
        element={<FileManagementPage fileCategory="boat" />}
      />
    </Route>
  );
}

export function AdminRoutes() {
  const { adminNavigation } = config;
  return (
    <Routes>
      <Route path="/auth/*" element={<AuthenticationLayout microsoftLogin />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <DashboardLayout
              app="admin"
              navItems={adminNavigation}
              enableTimeMachine={
                process.env.NX_ENABLE_TIME_MACHINE === "true" ||
                process.env.NX_ENABLE_TIME_MACHINE === true
              }
            />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/applications" replace />} />
        <Route path="users" element={<UserLayout />}>
          <Route index element={<ListUsers />} />
          <Route
            path="*"
            element={
              <main style={{ padding: "1rem" }}>
                <p>Page in progress</p>
              </main>
            }
          />
        </Route>
        {getSailorRoutes()}
        <Route path="dealers" element={<DealerOutlet />}>
          <Route index element={<ListDealer />} />
          <Route path="create" element={<CreateDealer />} />
          <Route path=":dealerId" element={<EditDealer />} />
          <Route
            path=":dealerId/:fileGroup/files"
            element={<SailorFileManagement fileCategory="sailor" />}
          />
        </Route>
        <Route path="applications" element={<ApplicationsOutlet />}>
          <Route index element={<ListApplications />} />
          <Route
            path=":applicationId"
            element={
              <OutletWithCrumb
                to="/applications/:applicationId"
                docRef="/applications/{applicationId}"
                crumb={({ data: applications }) =>
                  applications?.applicationNumber ?? ""
                }
              />
            }
          >
            <Route index element={<ApplicationForm />} />
            <Route
              path=":fileGroup/files"
              element={<FileManagementPage fileCategory="application" />}
            />
            <Route
              path="creditUnderwriting"
              element={<CreditUnderwritingSection />}
            />
            {getSailorRoutes("/applications/:applicationId", true)}
            {getBoatRoutes("/applications/:applicationId", true)}
            {getAgreementSailorRoutes("/applications/:applicationId", true)}
          </Route>
        </Route>
        <Route path="agreements" element={<AgreementsOutlet />}>
          <Route index element={<ListAgreements />} />
          <Route
            path=":agreementId"
            element={
              <OutletWithCrumb
                to="/agreements/:agreementId"
                docRef="/agreements/{agreementId}"
                crumb={({ data: agreement }) => agreement.agreementNumber}
              />
            }
          >
            <Route index element={<EditAgreement />} />
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="settlements" element={<SettlementsPage />} />
            <Route path="arrears" element={<ArrearsMgmt />} />
          </Route>
        </Route>
        {getBoatRoutes()}
        <Route path="interest-rates" element={<Rates />} />
        <Route path="reports/cais" element={<CaisReport />} />
        <Route path="profile" element={<AdminProfile />} />
      </Route>
      <Route
        path="*"
        element={
          <main style={{ padding: "1rem" }}>
            <p>There's nothing here!</p>
          </main>
        }
      />
    </Routes>
  );
}
